import {
  ArrowPathIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  PlusCircleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import { generatePath } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { Container } from '@/components/legacy-ui';
import { CanonicalTable } from '@/components/table/CanonicalTable';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { Button } from '@/components/ui/button';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useSortableColumn } from '@/hooks/useSortableColumn';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import {
  type DeletionRecord,
  useCancelTenantDeletion,
} from '@/hooks/useTenantDeletionMutations';
import { useTenantDeletion } from '@/hooks/useTenantDeletionStatus';
import { CustomerActions } from '@/sections/customerManagement/components/actions/CustomerActions';
import { CustomerManagementSearchFilters } from '@/sections/customerManagement/components/CustomerManagementSearchFilters';
import {
  AddCustomerModal,
  DeleteCustomersModal,
  UpdateCustomerTypeModal,
} from '@/sections/customerManagement/components/modals';
import {
  getColumns,
  getSortableColumnsConfig,
} from '@/sections/customerManagement/config/columnDefinitions';
import { CUSTOMER_TABLE_CONFIG } from '@/sections/customerManagement/config/columns';
import {
  useCustomerManagement,
  UseCustomerManagementOptions,
} from '@/sections/customerManagement/hooks/useCustomerManagement';
import { CustomerData } from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { CustomerTypeValue, GraphQuery } from '@/types';
import { encode } from '@/utils/encrypt.util';

interface CustomerTableProps {
  customerFilter?: UseCustomerManagementOptions;
  addButtonLabel?: string;
  defaultCustomerType?: CustomerTypeValue;
  emptyStateTitle?: string;
  headerActions?: React.ReactNode;
}

const CustomerTableComponent: React.FC<CustomerTableProps> = ({
  customerFilter = { excludeTypes: ['BUG_BOUNTY'] },
  addButtonLabel = 'Add Customer',
  defaultCustomerType,
  emptyStateTitle = 'No customers found',
  headerActions,
}) => {
  const { startImpersonation, isPraetorianUser } = useAuth();
  const { canOnboardCustomer } = useEntitlements();
  const [addCustomerModal, setAddCustomerModal] = useState(false);
  const [rowActionCustomer, setRowActionCustomer] =
    useState<CustomerData | null>(null);
  const [rowActionModal, setRowActionModal] = useState<
    'updateType' | 'delete' | null
  >(null);
  const [cancelTarget, setCancelTarget] = useState<DeletionRecord | null>(null);
  const queryClient = useQueryClient();
  const [sort, setSort] = useState<{
    orderBy: keyof CustomerData;
    descending: boolean;
  }>({
    orderBy: 'displayName',
    descending: false,
  });

  const { drawerState } = useGlobalState();
  const pendingDrawerEmailRef = useRef<string | null>(null);

  // Tenant-deletion row actions (Praetorian-only). Cancel operates on the record
  // key (id); the certificate download fetches the deletion detail on demand and
  // opens the presigned `certificateUrl` — there is no separate certificate
  // endpoint (architecture A12).
  const cancelDeletion = useCancelTenantDeletion();
  // The certificate tab MUST be opened synchronously inside the click handler to
  // survive popup blockers: the presigned URL comes from an on-demand fetch, and
  // an async gap before `window.open` drops the user-gesture context (Safari in
  // particular silently blocks the popup). So the row action opens a blank tab in
  // `onClick`, stores the reference here, and this effect redirects that tab once
  // the detail query resolves — or closes it and toasts on failure.
  const certificateWindowRef = useRef<Window | null>(null);
  const [certificateDeletionId, setCertificateDeletionId] = useState<
    string | null
  >(null);
  const certificateQuery = useTenantDeletion(certificateDeletionId ?? '');

  useEffect(() => {
    if (!certificateDeletionId) return;
    const certWindow = certificateWindowRef.current;
    const url = certificateQuery.data?.certificateUrl;

    if (url) {
      // Redirect the already-open (gesture-created) tab to the presigned URL.
      if (certWindow) certWindow.location.href = url;
    } else if (certificateQuery.isError || certificateQuery.isSuccess) {
      // Resolved with no certificate URL, or errored — abandon the blank tab.
      certWindow?.close();
      toast.error('Failed to load deletion certificate');
    } else {
      return; // still loading — wait for the next resolution
    }

    certificateWindowRef.current = null;
    // Clear the trigger token so the same certificate can be re-downloaded on a
    // subsequent click. Sanctioned set-state-in-effect: syncing an external
    // system (the opened browser tab) back into React.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCertificateDeletionId(null);
  }, [
    certificateDeletionId,
    certificateQuery.data,
    certificateQuery.isError,
    certificateQuery.isSuccess,
  ]);

  const handleCustomerClick = useCallback(
    (customer: CustomerData) => {
      drawerState.handleOpenCustomerDrawer(customer);
    },
    [drawerState]
  );

  const handleCustomerOpenInNewTab = useCallback((customer: CustomerData) => {
    const url = generatePath(`/:userId/${AuthenticatedRoute.INSIGHTS}`, {
      userId: encode(customer.email),
    });
    window.open(url, '_blank');
  }, []);

  const handleViewAccount = useCallback(
    (customer: CustomerData) => {
      startImpersonation(customer.email, AuthenticatedRoute.INSIGHTS);
    },
    [startImpersonation]
  );

  // Add this new handler after handleViewAccount
  const handleRowClickWithModifiers = useCallback(
    (
      customer: CustomerData,
      idx: number,
      event?: React.MouseEvent<HTMLTableRowElement>
    ) => {
      // Check for cmd/ctrl key for "open in new tab"
      if (event?.metaKey || event?.ctrlKey) {
        handleCustomerOpenInNewTab(customer);
      } else {
        handleCustomerClick(customer);
      }
    },
    [handleCustomerClick, handleCustomerOpenInNewTab]
  );

  const { defaultColumns, defaultConfig } = useMemo(() => {
    const columns = getColumns({
      onViewAccount: handleViewAccount,
      isPraetorianUser,
    });

    return {
      defaultColumns: columns,
      defaultConfig: getSortableColumnsConfig({ defaultColumns: columns }),
    };
  }, [handleViewAccount, isPraetorianUser]);

  const { activeColumns, setActiveColumns, columns } = useSortableColumn({
    key: CUSTOMER_TABLE_CONFIG.storageKey,
    defaultColumns,
    defaultConfig,
  });

  const {
    filters,
    setFilters,
    customers,
    allCustomers, // Raw unfiltered customer list
    customersStatus,
    totalCustomers,
  } = useCustomerManagement(customerFilter);

  const handleCustomerCreated = useCallback(
    async (createdEmail: string) => {
      try {
        // Wait for BOTH queries to complete BEFORE setting pending state
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: getQueryKey.getMy('account'),
          }),
          queryClient.refetchQueries({
            queryKey: getQueryKey.getMy(
              'setting',
              undefined,
              undefined,
              undefined,
              undefined,
              true
            ),
          }),
        ]);

        // Clear search filter so new customer is visible in table
        setFilters(prev => ({ ...prev, search: '' }));

        // NOW set pending email - both queries complete, filters cleared
        pendingDrawerEmailRef.current = createdEmail;
      } catch (error) {
        console.error('Failed to refetch customer data:', error);
      }
    },
    [queryClient, setFilters]
  );

  // Open drawer when a newly created customer appears in the list
  useEffect(() => {
    if (!pendingDrawerEmailRef.current) return;

    // Use allCustomers (unfiltered) to avoid race condition with filter clearing
    const customer = allCustomers.find(
      c => c.email === pendingDrawerEmailRef.current
    );

    if (customer) {
      // Open drawer on settings tab for immediate configuration
      drawerState.handleOpenCustomerDrawer(customer, {
        initialTab: 'settings',
      });
      pendingDrawerEmailRef.current = null;
      return;
    }

    // Fallback: Clear pending state after 5 seconds
    const pendingEmail = pendingDrawerEmailRef.current;
    const timeoutId = setTimeout(() => {
      console.warn(`Customer ${pendingEmail} not found after 5 seconds`);
      pendingDrawerEmailRef.current = null;
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [allCustomers, drawerState]);

  // Calculate if filters return empty results when data exists
  const isFilteredEmpty =
    customersStatus === 'success' &&
    customers.length === 0 &&
    totalCustomers > 0;

  // Force table remount when transitioning from empty filtered state to data state
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    customers.length
  );

  // Sort customers based on current sort state
  const sortedCustomers = useMemo(() => {
    if (!sort) return customers;

    return [...customers].sort((a, b) => {
      const aValue = a[sort.orderBy];
      const bValue = b[sort.orderBy];

      // Handle undefined/null values
      // For lastCompromiseDate, treat "Never" (undefined) as the oldest date
      if (aValue === undefined || aValue === null) {
        if (bValue === undefined || bValue === null) return 0;
        if (sort.orderBy === 'lastCompromiseDate') {
          return sort.descending ? 1 : -1;
        }
        return 1;
      }
      if (bValue === undefined || bValue === null) {
        if (sort.orderBy === 'lastCompromiseDate') {
          return sort.descending ? -1 : 1;
        }
        return -1;
      }

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue
          .toLowerCase()
          .localeCompare(bValue.toLowerCase());
        return sort.descending ? -comparison : comparison;
      }

      // Number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort.descending ? bValue - aValue : aValue - bValue;
      }

      // Default comparison (convert to string)
      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return sort.descending ? -comparison : comparison;
    });
  }, [customers, sort]);

  // Map customers to { key: string } shape required by useBulkSelection
  // CustomerData uses email as the unique identifier — map it to key
  const customersWithKey = useMemo(
    () => sortedCustomers.map(c => ({ ...c, key: c.email })),
    [sortedCustomers]
  );

  // Row selection management — uses useBulkSelection for Gmail-style two-step selection
  const bulkSelection = useBulkSelection({
    data: customersWithKey,
    activeFilters: {} as GraphQuery,
    filterVersion: filters,
  });

  const {
    selectedRows,
    setSelectedRows,
    hasSelection,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
  } = bulkSelection;

  // Memoize row actions to prevent unnecessary re-renders
  const rowActions = useCallback(
    (customer: CustomerData) => {
      const items: {
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
      }[] = [
        {
          label: 'View Account',
          icon: <EyeIcon className="size-5" />,
          onClick: () => startImpersonation(customer.email),
        },
        {
          label: 'Update Type',
          icon: <ArrowPathIcon className="size-5" />,
          onClick: () => {
            setRowActionCustomer(customer);
            setRowActionModal('updateType');
          },
        },
      ];

      // The entire deletion surface (delete / cancel / certificate) is
      // Praetorian-only — defense-in-depth for A14. Non-Praetorian parent-tenant
      // users reach this table but must not see a real tenant-purge control;
      // backend C1 SuperAdmin 403 remains authoritative.
      if (isPraetorianUser) {
        items.push({
          label: 'Delete Customer',
          icon: <TrashIcon className="size-5" />,
          onClick: () => {
            setRowActionCustomer(customer);
            setRowActionModal('delete');
          },
        });

        const deletion = customer.deletion;

        // Cancel only a pre-purge deletion; backend 409s otherwise (surfaced
        // verbatim). Operates on the record key (id), not the email (A12).
        if (
          deletion &&
          (deletion.state === 'requested' || deletion.state === 'scheduled')
        ) {
          items.push({
            label: 'Cancel scheduled deletion',
            icon: <XCircleIcon className="size-5" />,
            onClick: () => setCancelTarget(deletion),
          });
        }

        // Certificate is available only for a completed purge; the presigned URL
        // comes from the deletion detail fetched on click (A12).
        if (deletion && deletion.state === 'completed') {
          items.push({
            label: 'Download deletion certificate',
            icon: <DocumentArrowDownIcon className="size-5" />,
            onClick: () => {
              if (certificateDeletionId) return; // a certificate fetch is already in flight
              // Open the tab synchronously (popup-blocker-safe); the effect above
              // redirects it once the presigned URL resolves. Null the opener to
              // guard against reverse tabnabbing on the cross-origin S3 URL.
              const certWindow = window.open('', '_blank');
              if (certWindow) certWindow.opener = null;
              certificateWindowRef.current = certWindow;
              setCertificateDeletionId(deletion.key);
            },
          });
        }
      }

      return { menu: { items } };
    },
    [
      startImpersonation,
      isPraetorianUser,
      cancelDeletion,
      certificateDeletionId,
    ]
  );

  return (
    <>
      <Container className="bg-chariot-background-primary/60 border-chariot-stroke flex h-full flex-1 flex-col rounded-md border-2 p-6 pb-0">
        <div className="flex h-full w-full flex-col gap-4">
          <div className="flex w-full flex-row items-center justify-between">
            <CustomerManagementSearchFilters
              filters={filters}
              setFilters={setFilters}
            />
            {isPraetorianUser &&
              (!defaultCustomerType || canOnboardCustomer) && (
                <div className="flex items-center gap-2">
                  {headerActions}
                  <Button size="sm" onClick={() => setAddCustomerModal(true)}>
                    <PlusCircleIcon className="size-5" />
                    {addButtonLabel}
                  </Button>
                </div>
              )}
          </div>

          {/* Customer Actions Toolbar */}
          {hasSelection && (
            <CustomerActions
              customers={sortedCustomers}
              selectedRows={selectedRows}
              onClearSelection={clearSelection}
            />
          )}

          {/* SelectionBanner for Gmail-style two-step bulk selection */}
          <SelectionBanner
            selectedCount={selectedCount}
            totalLoadedCount={customersWithKey.length}
            hasNextPage={false}
            isSelectAll={isSelectAll}
            isAllLoadedSelected={isAllLoadedSelected}
            resourceLabel="customers"
            onSelectAllMatching={selectAllMatching}
            onClearSelection={clearSelection}
          />

          <div className="flex min-h-0 flex-1 flex-col gap-4 pb-6">
            <div className="flex-1 overflow-auto">
              <CanonicalTable
                key={tableKey}
                name="customer-management"
                responsive={false}
                rowHeight={CUSTOMER_TABLE_CONFIG.defaultRowHeight}
                columns={columns}
                sortableColumns={{
                  activeColumns,
                  setActiveColumns,
                  defaultConfig,
                }}
                data={customersWithKey}
                error={null}
                status={customersStatus}
                loadingRowCount={3}
                noData={{
                  title: emptyStateTitle,
                }}
                selection={{
                  value: selectedRows,
                  onChange: setSelectedRows,
                  selectAll: true,
                }}
                rowActions={rowActions}
                sort={{
                  orderBy: sort.orderBy,
                  descending: sort.descending,
                  onSort: newSort => setSort(newSort),
                }}
                onRowClick={handleRowClickWithModifiers}
              />
            </div>
          </div>
        </div>
      </Container>

      <AddCustomerModal
        isOpen={addCustomerModal}
        onClose={() => setAddCustomerModal(false)}
        onSuccess={handleCustomerCreated}
        defaultCustomerType={defaultCustomerType}
      />

      {rowActionModal === 'updateType' && rowActionCustomer && (
        <UpdateCustomerTypeModal
          isOpen={true}
          onClose={() => {
            setRowActionModal(null);
            setRowActionCustomer(null);
          }}
          customers={[rowActionCustomer]}
          source="table"
          onSuccess={() => {
            setRowActionModal(null);
            setRowActionCustomer(null);
          }}
        />
      )}

      {rowActionModal === 'delete' && rowActionCustomer && (
        <DeleteCustomersModal
          isOpen={true}
          onClose={() => {
            setRowActionModal(null);
            setRowActionCustomer(null);
          }}
          customers={[rowActionCustomer]}
          source="table"
          onSuccess={() => {
            setRowActionModal(null);
            setRowActionCustomer(null);
          }}
        />
      )}

      <ConfirmationDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => {
          if (!cancelTarget) return;
          const target = cancelTarget;
          toast.promise(cancelDeletion.mutateAsync(target.key), {
            loading: 'Cancelling deletion…',
            success: 'Tenant deletion cancelled',
            error: (err: unknown) =>
              err instanceof Error && err.name && err.name !== 'Error'
                ? err.name
                : 'Failed to cancel tenant deletion',
          });
          setCancelTarget(null);
        }}
        variant="destructive"
        title="Cancel scheduled deletion"
        description={
          cancelTarget
            ? `Cancel the ${cancelTarget.state} deletion for ${cancelTarget.tenant}? The tenant will be unfrozen.`
            : ''
        }
        confirmLabel="Cancel deletion"
        cancelLabel="Keep deletion"
        isLoading={cancelDeletion.isPending}
        loadingLabel="Cancelling…"
        data-testid="cancel-tenant-deletion-dialog"
      />
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const CustomerTable = React.memo(CustomerTableComponent);
